import { WebViewerInstance } from '@pdftron/webviewer';
import { useCallback, useEffect } from 'react';

import { PART_FILE_UNITS, PartFileUnits } from '@frontend/api';

export const useUnitsPicker = ({
  instance,
  printUnits,
  onUpdatePrintUnits,
}: {
  instance: WebViewerInstance | null;
  printUnits: PartFileUnits;
  onUpdatePrintUnits?: (units: PartFileUnits) => void;
}) => {
  // Custom hook to add units picker to the WebViewer header
  const addUnitsPicker = useCallback(
    (units: PartFileUnits, onUpdateUnits?: (units: PartFileUnits) => void) => {
      if (!instance || !onUpdateUnits) return;

      const { UI } = instance;

      // Create the units picker custom element following Apryse docs
      const unitsPickerElement = new UI.Components.CustomElement({
        dataElement: 'unitsPicker',
        className: 'units-picker-custom-element',
        render: () => {
          const container = document.createElement('div');

          const label = document.createElement('span');
          label.textContent = 'Units';

          const select = document.createElement('select');

          // Create options
          const mmOption = document.createElement('option');
          mmOption.value = PART_FILE_UNITS.MM;
          mmOption.textContent = PART_FILE_UNITS.MM;
          mmOption.selected = units === PART_FILE_UNITS.MM;

          const inOption = document.createElement('option');
          inOption.value = PART_FILE_UNITS.IN;
          inOption.textContent = PART_FILE_UNITS.IN;
          inOption.selected = units === PART_FILE_UNITS.IN;

          select.appendChild(mmOption);
          select.appendChild(inOption);

          // Add change event listener
          select.addEventListener('change', (event) => {
            const target = event.target as HTMLSelectElement;
            const newUnits = target.value as PartFileUnits;
            onUpdateUnits(newUnits);
          });

          container.appendChild(label);
          container.appendChild(select);

          return container;
        },
      });

      // Get the default header and add the units picker
      const defaultHeader = UI.getModularHeader('default-top-header');
      const currentItems = defaultHeader.getItems();

      // Remove existing units picker and divider if they exist
      const existingItems = currentItems.filter(
        (item: any) =>
          item.dataElement !== 'unitsPicker' &&
          item.dataElement !== 'unitsPickerDivider',
      );

      // Add divider before units picker
      const newDivider = {
        type: 'divider',
        dataElement: 'unitsPickerDivider',
        hidden: ['mobile'],
      };

      // Always put divider and units picker last
      existingItems.push(newDivider);
      existingItems.push(unitsPickerElement);

      // NOTE: this causes a ton of warnings in the console, perhaps there is a better way to
      //  more selectively insert the units picker
      defaultHeader.setItems(existingItems);
    },
    [instance],
  );

  // Add units picker to the WebViewer header
  useEffect(() => {
    if (instance && onUpdatePrintUnits) {
      addUnitsPicker(printUnits, onUpdatePrintUnits);
    }
  }, [instance, printUnits, onUpdatePrintUnits, addUnitsPicker]);
};
